import React, { useState, useEffect } from 'react';
import './Transactions.css';
import { useParams } from 'react-router-dom';
import SectionCard from '../../../../../Components/SectionCard/SectionCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faFileAlt, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import FinancialService from "../../../../../AppSettings/services/FinancialService";
import VenExpService from "../../../../../AppSettings/services/VenExpService";
import OppExpService from "../../../../../AppSettings/services/OppExpService";
import GenExpensesService from "../../../../../AppSettings/services/GenExpensesService";
import FixBillService from "../../../../../AppSettings/services/FixBillService";
import InvoiceService from '../../../../../AppSettings/services/InvoiceService';
import { buildInvoiceNumberMap, resolveInvoiceNumber } from '../../../../../helpers/invoiceNumber';
import { normalizeToArray } from '../../../../../helpers/responseHelpers';
import { parseLocalDate } from '../../../../../helpers/dateHelpers';
import { computeBilledTimeAmount } from '../../../../../helpers/billingHelpers';

interface Transaction {
  id: string;
  type: string;
  displayType: 'Payment Received' | 'Expense Recorded' | 'Invoice Generated';
  amount: number;
  description: string;
  date: Date;
  employee: string;
  category: 'income' | 'expense' | 'invoice';
}

// Format actual posting date (e.g., "Feb 23, 2025, 2:30 PM")
function formatPostingDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const Transactions: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAllData = async (): Promise<void> => {
      if (!matterId) return;
      
      try {
        setIsLoading(true);
        setError(null);

        // Fetch all data in parallel using matter-specific endpoints
        const [
          billedTimeData,
          fixedFeesData,
          otherBillingData,
          vendorData,
          operatingData,
          generalData,
          fixedBillsData,
          invoicesData
        ] = await Promise.all([
          FinancialService.getBilledTime(matterId),
          FinancialService.getAssignedFixedFees(matterId),
          FinancialService.getOtherBillingByMatter(matterId),
          VenExpService.getVenExpensesByMatter(matterId),
          OppExpService.getOppExpensesByMatter(matterId),
          GenExpensesService.getGenExpensesByMatter(matterId),
          FixBillService.getFixBillExpensesByMatter(matterId),
          InvoiceService.getInvoicesByMatterId(matterId).catch(() => [])
        ]);

        // Format all data into a consistent structure
        const formattedTransactions: Transaction[] = [];

        // Normalize all sources to arrays and format
        const billedEntries = normalizeToArray(billedTimeData);
        const fixedFeesList = normalizeToArray(fixedFeesData as any);
        const otherBillingList = normalizeToArray(otherBillingData);
        const vendorList = normalizeToArray(vendorData);
        const operatingList = normalizeToArray(operatingData);
        const generalList = normalizeToArray(generalData);
        const fixedBillsList = normalizeToArray(fixedBillsData);
        const invoicesList = normalizeToArray(invoicesData);
        const invoiceNumberMap = buildInvoiceNumberMap(invoicesList);

        // Format billed time data (API returns date_recorded)
        billedEntries.forEach((item: any, index: number) => {
          const dateValue = item.date_recorded ?? item.date;
          formattedTransactions.push({
            id: `BT-${item.bt_id ?? item.id ?? index}`,
            type: "Billable Time",
            displayType: "Payment Received",
            amount: computeBilledTimeAmount(item),
            description: `$${item.billedrate}/hr - ${item.time_recorded}`,
            date: dateValue ? parseLocalDate(dateValue) : new Date(),
            employee: item.employee_name || "Unknown",
            category: "income"
          });
        });

        // Format fixed fees data
        fixedFeesList.forEach((item: any, index: number) => {
          formattedTransactions.push({
            id: `FF-${item.id ?? item.fee_code ?? index}`,
            type: "Fixed Fees",
            displayType: "Payment Received",
            amount: Number.isFinite(parseFloat(item.fees)) ? parseFloat(item.fees) : 0,
            description: `${item.fee_code} - ${item.narrative}`,
            date: parseLocalDate(item.date_added),
            employee: "N/A",
            category: "income"
          });
        });

        // Format other billing
        otherBillingList.forEach((item: any, index: number) => {
          formattedTransactions.push({
            id: `OB-${item.ob_id ?? item.id ?? index}`,
            type: "Other Billing",
            displayType: "Payment Received",
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            description: item.description || item.narrative || "",
            date: (item.date ?? item.date_added) ? parseLocalDate(item.date ?? item.date_added) : new Date(),
            employee: item.employee_name || "N/A",
            category: "income"
          });
        });

        // Format vendor expenses
        vendorList.forEach((item: any) => {
          formattedTransactions.push({
            id: `VE-${item.ven_id}`,
            type: "Vendor Services",
            displayType: "Expense Recorded",
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            description: item.description,
            date: parseLocalDate(item.date_added),
            employee: "Unknown Agent",
            category: "expense"
          });
        });

        // Format operating expenses
        operatingList.forEach((item: any) => {
          formattedTransactions.push({
            id: `OE-${item.opp_id}`,
            type: "Operating",
            displayType: "Expense Recorded",
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            description: item.description,
            date: parseLocalDate(item.date_added),
            employee: "Unknown Agent",
            category: "expense"
          });
        });

        // Format general expenses
        generalList.forEach((item: any) => {
          formattedTransactions.push({
            id: `GE-${item.gen_id}`,
            type: "General",
            displayType: "Expense Recorded",
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            description: item.description,
            date: parseLocalDate(item.date_added),
            employee: "Unknown Agent",
            category: "expense"
          });
        });

        // Format fixed bills
        fixedBillsList.forEach((item: any, index: number) => {
          formattedTransactions.push({
            id: `FB-${item.bill_id ?? index}`,
            type: "Fixed Bills",
            displayType: "Expense Recorded",
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            description: item.description,
            date: parseLocalDate(item.date_added),
            employee: "Unknown Agent",
            category: "expense"
          });
        });

        // Format invoices - only show invoices that haven't been paid yet (Invoice Generated)
        invoicesList.forEach((item: any) => {
          const totalAmount = Number.isFinite(parseFloat(item.total_amount)) ? parseFloat(item.total_amount) : 0;
          if (totalAmount <= 0) return;
          // `amount_paid`/`paid` reflect real payments (acc_rec_total is the AR subtotal, not payments).
          const paid = parseFloat(item.amount_paid || 0) || 0;
          const isPaid = item.paid === true || paid >= totalAmount;
          if (!isPaid) {
            formattedTransactions.push({
              id: `IN-${item.invoiceid}`,
              type: "Invoice",
              displayType: "Invoice Generated",
              amount: totalAmount,
              description: `Invoice #${resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)}`,
              // Invoices expose `created_at` (not date_added); falling back to now misorders them.
              date: item.created_at ? parseLocalDate(item.created_at) : new Date(),
              employee: "N/A",
              category: "invoice"
            });
          } else if (item.payment_completed_time) {
            formattedTransactions.push({
              id: `IN-PAY-${item.invoiceid}`,
              type: "Invoice Payment",
              displayType: "Payment Received",
              amount: totalAmount,
              description: `Invoice #${resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)}`,
              date: new Date(item.payment_completed_time),
              employee: "N/A",
              category: "income"
            });
          }
        });

        // Sort by date (most recent first) and take only last 5
        const sortedTransactions = formattedTransactions
          .sort((a, b) => b.date.getTime() - a.date.getTime())
          .slice(0, 5);
        setTransactions(sortedTransactions);
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError("Failed to fetch transactions data.");
      } finally {
        setIsLoading(false);
      }
    };

    if (matterId) {
      fetchAllData();
    }
  }, [matterId]);

  const getTransactionIcon = (category: string): IconDefinition => {
    switch (category) {
      case 'income':
        return faArrowDown;
      case 'expense':
        return faArrowUp;
      case 'invoice':
        return faFileAlt;
      default:
        return faFileAlt;
    }
  };

  const getIconClass = (category: string): string => {
    switch (category) {
      case 'income':
        return 'matter-transaction-icon--income';
      case 'expense':
        return 'matter-transaction-icon--expense';
      case 'invoice':
        return 'matter-transaction-icon--invoice';
      default:
        return 'matter-transaction-icon--invoice';
    }
  };

  const formatAmount = (amount: number, category: string): string => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

    if (category === 'income') {
      return `+${formatted}`;
    } else if (category === 'expense') {
      return `-${formatted}`;
    } else {
      return formatted;
    }
  };

  return (
    <SectionCard
      title="Recent Transactions"
      right={<a href={`/matterDetails/${matterId}/financial/transactions`} className="matter-view-all-link">View All</a>}
      className="matter-recent-transactions"
    >
      {isLoading ? (
        <p>Loading transactions...</p>
      ) : error ? (
        <p className="matter-error-message">{error}</p>
      ) : (
        <div className="matter-transactions-list">
          {transactions.map((transaction, index) => {
            const isLast = index === transactions.length - 1;
            return (
              <div
                key={transaction.id}
                className={`matter-transaction-item ${transaction.category} ${isLast ? 'matter-transaction-item--last' : ''}`}
              >
                <div className={`matter-transaction-icon-container ${getIconClass(transaction.category)}-container`}>
                  <FontAwesomeIcon
                    icon={getTransactionIcon(transaction.category)}
                    className={`matter-transaction-icon ${getIconClass(transaction.category)}`}
                  />
                </div>
                <div className="matter-transaction-details">
                  <div className="matter-transaction-type">{transaction.type}</div>
                  <div className="matter-transaction-description">{transaction.description}</div>
                </div>
                <div className="matter-transaction-amount-container">
                  <div className={`matter-transaction-amount ${transaction.category}`}>
                    {formatAmount(transaction.amount, transaction.category)}
                  </div>
                  <div className="matter-transaction-time">{formatPostingDate(transaction.date)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
};

export default Transactions;
